import { defineMessages } from './define'

/* The clinical phone screens: the doctor's consulting room (queue, notes,
 * prescription pad, tests, signing) and the desk's visit screen (vitals,
 * payment). What the doctor types, drug and test names, dose notation and
 * the printed prescription and receipt stay as they are - English. */

export default defineMessages({
  en: {
    'clinical.visitTitle': 'Visit',
    'clinical.saveDraft': 'Save draft',
    'clinical.moreHistory': 'History and examination',
    'clinical.addMedicine': 'Add medicine',
    'clinical.addTest': 'Add test',
    'clinical.vPulseLabel': 'Pulse',
    'clinical.vTempLabel': 'Temp',
    'clinical.vRoomAir': 'Room air',
    // shared by both screens
    'clinical.token': 'Token #{n}',
    'clinical.visitNo': 'Visit #{n}',
    'clinical.couldNotSave': 'Could not save.',
    'clinical.chronicNote': 'Chronic: {text}',
    'clinical.vWt': 'Wt {v}',
    'clinical.vWtKg': 'Wt {v} kg',
    'clinical.vHtCm': 'Ht {v} cm',
    'clinical.vPulse': 'Pulse {v}',
    'clinical.vTemp': 'Temp {v}',
    'clinical.vTempF': 'Temp {v}°F',
    'clinical.vSugar': 'Sugar {v} {type}',

    // consulting room: queue
    'clinical.consultingRoom': 'Consulting room',
    'clinical.todaysQueue': 'Today’s queue',
    'clinical.toSee': '{n} to see',
    'clinical.nobodyWaiting': 'Nobody waiting',
    'clinical.nobodyWaitingBody': 'Reception opens visits from the front desk.',
    'clinical.couldNotOpenVisit': 'Could not open that visit.',

    // consulting room: the pad
    'clinical.reopen': 'Reopen',
    'clinical.working': 'Working…',
    'clinical.closeAndPrint': 'Close & print',
    'clinical.draft': 'Draft',
    'clinical.backToQueue': 'Back to queue',
    'clinical.previewSheet': 'Preview the printed sheet',
    'clinical.noVitals': 'No vitals recorded',
    'clinical.allergiesBanner': 'Allergies: {text}',
    'clinical.closedPrinted': 'Consultation closed. Printed {n}× so far.',
    'clinical.templates': 'Templates',
    'clinical.removeOne': 'Remove one',
    'clinical.repeatLast': 'Repeat last prescription',
    'clinical.chiefComplaints': 'Chief complaints',
    'clinical.hpi': 'History of present illness',
    'clinical.examFindings': 'Examination findings',
    'clinical.provisionalDx': 'Provisional diagnosis',
    'clinical.icd10': 'ICD-10 code',
    'clinical.pastHistory': 'Past history',
    'clinical.familyHistory': 'Family history',
    'clinical.medicines': 'Medicines',
    'clinical.onThePad': '{n} on the pad',
    'clinical.checkAllergy': 'Check the allergy note before signing',
    'clinical.allergyWarnNote': 'Recorded allergy: {allergies}. Name match only — sign anyway if you know it is safe for this patient.',
    'clinical.removeItem': 'Remove {name}',
    'clinical.removeMedicineN': 'Remove medicine {n}',
    'clinical.removeTestN': 'Remove test {n}',
    'clinical.drugPlaceholder': 'Brand or generic name',
    'clinical.drugNotListed': 'Not in the drug list — it will still print as typed.',
    'clinical.allergyRow': 'Allergy: {text}',
    'clinical.morning': 'Morning',
    'clinical.afternoon': 'Afternoon',
    'clinical.night': 'Night',
    'clinical.morningShort': 'M',
    'clinical.afternoonShort': 'A',
    'clinical.nightShort': 'N',
    'clinical.timing': 'Timing',
    'clinical.timingPick': 'Timing —',
    'clinical.durationDays': 'Duration in days',
    'clinical.days': 'days',
    'clinical.quantity': 'Quantity',
    'clinical.qty': 'Qty',
    'clinical.note': 'Note',
    'clinical.noteOptional': 'Note (optional)',
    'clinical.dispense': 'Dispense {qty}',
    'clinical.testsAdvised': 'Tests advised',
    'clinical.ordered': '{n} ordered',
    'clinical.testName': 'Test name',
    'clinical.preparation': 'Preparation: {text}',
    'clinical.urgency': 'Urgency',
    'clinical.instruction': 'Instruction',
    'clinical.advice': 'Advice',
    'clinical.adviceHint': 'Diet, activity, warning signs.',
    'clinical.followUpDays': 'Follow-up after (days)',
    'clinical.printsAs': 'Prints as {date}',
    'clinical.savePadAsTemplate': 'Save pad as template',
    'clinical.templateName': 'Template name',
    'clinical.saveTemplate': 'Save template',
    'clinical.saveAsTemplate': 'Save as template',
    'clinical.addendum': 'Addendum',
    'clinical.addendumNote': 'A signed prescription is never edited. Corrections are appended and dated.',
    'clinical.addendumAria': 'Addendum note',
    'clinical.addNote': 'Add a note',
    'clinical.append': 'Append',
    'clinical.previousVisits': 'Previous visits',
    'clinical.firstVisit': 'First visit to the clinic.',
    'clinical.timesDays': '× {n} days',
    'clinical.noMedicines': 'No medicines recorded.',
    'clinical.removeTemplateTitle': 'Remove a template',
    'clinical.removeTemplateNote': 'Removing one leaves every prescription already written from it untouched.',
    'clinical.keepThem': 'Keep them',
    'clinical.reopenTitle': 'Reopen for edit',
    'clinical.reopenNote': 'This goes on the record as a dated addendum. The visit returns to in-consult.',
    'clinical.reopenReasonAria': 'Reason for reopening',
    'clinical.reopenReasonPh': 'Why is it being reopened?',
    'clinical.keepClosed': 'Keep it closed',

    // consulting room: toasts and the allergy confirm
    'clinical.draftSaved': 'Draft saved.',
    'clinical.allergyConfirm': '{name} is recorded as allergic to {allergies}.\n\n{conflicts}\n\nSign and print anyway?',
    'clinical.signedPrinting': 'Signed. Sending to the printer.',
    'clinical.reopened': 'Reopened. Edit and close again when done.',
    'clinical.addendumAdded': 'Addendum added.',
    'clinical.templateSaved': 'Saved as a template.',
    'clinical.templateRemoved': 'Removed "{name}".',
    'clinical.templateApplied': 'Applied "{name}".',
    'clinical.carriedForward_one': 'Carried forward {n} medicine from {date}.',
    'clinical.carriedForward_other': 'Carried forward {n} medicines from {date}.',

    // visit desk
    'clinical.enterOneReading': 'Enter at least one reading.',
    'clinical.readingRecorded': 'Reading recorded.',
    'clinical.paymentRecorded': 'Payment recorded.',
    'clinical.couldNotRecordPayment': 'Could not record payment.',
    'clinical.allergyBanner': 'Allergy: {text}',
    'clinical.complaint': 'Complaint',
    'clinical.vitals': 'Vitals',
    'clinical.vitalsCount': 'Vitals · {n}',
    'clinical.receipt': 'Receipt',
    'clinical.payment': 'Payment',
    'clinical.readings': 'Readings',
    'clinical.newestLast': 'newest last',
    'clinical.addingKeepsEarlier': 'Adding a reading keeps the earlier ones — a BP re-check is a new row, not an edit.',
    'clinical.leaveBlank': 'Leave anything not measured blank. BMI is computed, never typed.',
    'clinical.weightKg': 'Weight (kg)',
    'clinical.heightCm': 'Height (cm)',
    'clinical.systolic': 'Systolic',
    'clinical.diastolic': 'Diastolic',
    'clinical.pulseBpm': 'Pulse (bpm)',
    'clinical.tempF': 'Temp (°F)',
    'clinical.respRate': 'Resp. rate',
    'clinical.bloodSugar': 'Blood sugar',
    'clinical.sugarType': 'Sugar type',
    'clinical.fasting': 'Fasting',
    'clinical.postPrandial': 'Post-prandial',
    'clinical.random': 'Random',
    'clinical.addAnotherReading': 'Add another reading',
    'clinical.recordVitals': 'Record vitals',
    'clinical.bookedToPay': 'Booked to pay by {mode}. Change it below if they pay another way.',
    'clinical.bookedToPayQuoted': 'Booked to pay by {mode}, {amount} quoted. Change it below if they pay another way.',
    'clinical.consultationFee': 'Consultation fee',
    'clinical.rateFor': '{type} rate for {doctor}',
    'clinical.discount': 'Discount',
    'clinical.discountReason': 'Reason for discount',
    'clinical.netPayable': 'Net payable',
    'clinical.amountPaid': 'Amount paid',
    'clinical.mode': 'Mode',
    'clinical.referenceNo': 'Reference no.',
    'clinical.referencePh': 'UPI txn / card last 4',
    'clinical.recording': 'Recording…',
    'clinical.recordPayment': 'Record payment',
    'clinical.reference': 'Reference',
    'clinical.balanceDue': 'Balance due',
    'clinical.paymentReceipt': 'Payment receipt',
    'clinical.paymentsFinal': 'Payments cannot be edited. A refund or correction is recorded as a separate adjustment.',
    'clinical.latestVitals': 'Latest vitals on file: {list}.',
    'clinical.latestVitalsRecorded': 'Latest vitals on file: recorded.',
    'clinical.latestPulse': 'pulse {v}',
  },
  hi: {
    'clinical.visitTitle': 'विज़िट',
    'clinical.saveDraft': 'ड्राफ़्ट सहेजें',
    'clinical.moreHistory': 'इतिहास और जाँच',
    'clinical.addMedicine': 'दवा जोड़ें',
    'clinical.addTest': 'जाँच जोड़ें',
    'clinical.vPulseLabel': 'नब्ज़',
    'clinical.vTempLabel': 'तापमान',
    'clinical.vRoomAir': 'सामान्य हवा',
    'clinical.token': 'टोकन #{n}',
    'clinical.visitNo': 'विज़िट #{n}',
    'clinical.couldNotSave': 'सेव नहीं हो सका।',
    'clinical.chronicNote': 'पुरानी बीमारी: {text}',
    'clinical.vWt': 'वज़न {v}',
    'clinical.vWtKg': 'वज़न {v} kg',
    'clinical.vHtCm': 'लंबाई {v} cm',
    'clinical.vPulse': 'पल्स {v}',
    'clinical.vTemp': 'तापमान {v}',
    'clinical.vTempF': 'तापमान {v}°F',
    'clinical.vSugar': 'शुगर {v} {type}',

    'clinical.consultingRoom': 'कंसल्टिंग रूम',
    'clinical.todaysQueue': 'आज की कतार',
    'clinical.toSee': '{n} देखने बाकी',
    'clinical.nobodyWaiting': 'कोई इंतज़ार में नहीं',
    'clinical.nobodyWaitingBody': 'रिसेप्शन फ्रंट डेस्क से विज़िट खोलता है।',
    'clinical.couldNotOpenVisit': 'यह विज़िट खुल नहीं सकी।',

    'clinical.reopen': 'फिर से खोलें',
    'clinical.working': 'हो रहा है…',
    'clinical.closeAndPrint': 'बंद करें और प्रिंट करें',
    'clinical.draft': 'ड्राफ़्ट',
    'clinical.backToQueue': 'कतार पर वापस',
    'clinical.previewSheet': 'प्रिंट होने वाली शीट देखें',
    'clinical.noVitals': 'कोई वाइटल्स दर्ज नहीं',
    'clinical.allergiesBanner': 'एलर्जी: {text}',
    'clinical.closedPrinted': 'कंसल्टेशन बंद। अब तक {n} बार प्रिंट हुआ।',
    'clinical.templates': 'टेम्पलेट',
    'clinical.removeOne': 'एक हटाएँ',
    'clinical.repeatLast': 'पिछला प्रिस्क्रिप्शन दोहराएँ',
    'clinical.chiefComplaints': 'मुख्य शिकायतें',
    'clinical.hpi': 'वर्तमान बीमारी का इतिहास',
    'clinical.examFindings': 'जाँच के निष्कर्ष',
    'clinical.provisionalDx': 'प्रारंभिक डायग्नोसिस',
    'clinical.icd10': 'ICD-10 कोड',
    'clinical.pastHistory': 'पिछला इतिहास',
    'clinical.familyHistory': 'पारिवारिक इतिहास',
    'clinical.medicines': 'दवाइयाँ',
    'clinical.onThePad': 'पैड पर {n}',
    'clinical.checkAllergy': 'साइन करने से पहले एलर्जी नोट जाँचें',
    'clinical.allergyWarnNote': 'दर्ज एलर्जी: {allergies}। यह केवल नाम का मिलान है — अगर आपको पता है कि इस मरीज़ के लिए यह सुरक्षित है, तो फिर भी साइन करें।',
    'clinical.removeItem': '{name} हटाएँ',
    'clinical.removeMedicineN': 'दवा {n} हटाएँ',
    'clinical.removeTestN': 'टेस्ट {n} हटाएँ',
    'clinical.drugPlaceholder': 'ब्रांड या जेनेरिक नाम',
    'clinical.drugNotListed': 'दवा सूची में नहीं है — जैसा लिखा है वैसा ही प्रिंट होगा।',
    'clinical.allergyRow': 'एलर्जी: {text}',
    'clinical.morning': 'सुबह',
    'clinical.afternoon': 'दोपहर',
    'clinical.night': 'रात',
    'clinical.morningShort': 'सु',
    'clinical.afternoonShort': 'दो',
    'clinical.nightShort': 'रा',
    'clinical.timing': 'समय',
    'clinical.timingPick': 'समय —',
    'clinical.durationDays': 'कितने दिन',
    'clinical.days': 'दिन',
    'clinical.quantity': 'मात्रा',
    'clinical.qty': 'मात्रा',
    'clinical.note': 'नोट',
    'clinical.noteOptional': 'नोट (वैकल्पिक)',
    'clinical.dispense': 'दें: {qty}',
    'clinical.testsAdvised': 'सलाह दिए गए टेस्ट',
    'clinical.ordered': '{n} टेस्ट लिखे',
    'clinical.testName': 'टेस्ट का नाम',
    'clinical.preparation': 'तैयारी: {text}',
    'clinical.urgency': 'प्राथमिकता',
    'clinical.instruction': 'निर्देश',
    'clinical.advice': 'सलाह',
    'clinical.adviceHint': 'खान-पान, गतिविधि, खतरे के संकेत।',
    'clinical.followUpDays': 'फ़ॉलो-अप (कितने दिन बाद)',
    'clinical.printsAs': 'प्रिंट पर: {date}',
    'clinical.savePadAsTemplate': 'पैड को टेम्पलेट के रूप में सेव करें',
    'clinical.templateName': 'टेम्पलेट का नाम',
    'clinical.saveTemplate': 'टेम्पलेट सेव करें',
    'clinical.saveAsTemplate': 'टेम्पलेट के रूप में सेव करें',
    'clinical.addendum': 'एडेंडम',
    'clinical.addendumNote': 'साइन किया हुआ प्रिस्क्रिप्शन कभी बदला नहीं जाता। सुधार तारीख के साथ नीचे जोड़े जाते हैं।',
    'clinical.addendumAria': 'एडेंडम नोट',
    'clinical.addNote': 'नोट जोड़ें',
    'clinical.append': 'जोड़ें',
    'clinical.previousVisits': 'पिछली विज़िट',
    'clinical.firstVisit': 'क्लिनिक में पहली विज़िट।',
    'clinical.timesDays': '× {n} दिन',
    'clinical.noMedicines': 'कोई दवा दर्ज नहीं।',
    'clinical.removeTemplateTitle': 'टेम्पलेट हटाएँ',
    'clinical.removeTemplateNote': 'टेम्पलेट हटाने से उससे पहले लिखे गए प्रिस्क्रिप्शन पर कोई असर नहीं पड़ता।',
    'clinical.keepThem': 'रहने दें',
    'clinical.reopenTitle': 'बदलाव के लिए फिर से खोलें',
    'clinical.reopenNote': 'यह रिकॉर्ड में तारीख वाले एडेंडम के रूप में दर्ज होगा। विज़िट फिर से "डॉक्टर के पास" हो जाएगी।',
    'clinical.reopenReasonAria': 'फिर से खोलने का कारण',
    'clinical.reopenReasonPh': 'इसे फिर से क्यों खोल रहे हैं?',
    'clinical.keepClosed': 'बंद ही रहने दें',

    'clinical.draftSaved': 'ड्राफ़्ट सेव हो गया।',
    'clinical.allergyConfirm': '{name} को {allergies} से एलर्जी दर्ज है।\n\n{conflicts}\n\nफिर भी साइन और प्रिंट करें?',
    'clinical.signedPrinting': 'साइन हो गया। प्रिंटर पर भेज रहे हैं।',
    'clinical.reopened': 'फिर से खुल गया। बदलाव करके दोबारा बंद करें।',
    'clinical.addendumAdded': 'एडेंडम जोड़ दिया गया।',
    'clinical.templateSaved': 'टेम्पलेट के रूप में सेव हो गया।',
    'clinical.templateRemoved': '"{name}" हटा दिया गया।',
    'clinical.templateApplied': '"{name}" लगा दिया गया।',
    'clinical.carriedForward_one': '{date} से {n} दवा आगे ली गई।',
    'clinical.carriedForward_other': '{date} से {n} दवाइयाँ आगे ली गईं।',

    'clinical.enterOneReading': 'कम से कम एक रीडिंग डालें।',
    'clinical.readingRecorded': 'रीडिंग दर्ज हो गई।',
    'clinical.paymentRecorded': 'भुगतान दर्ज हो गया।',
    'clinical.couldNotRecordPayment': 'भुगतान दर्ज नहीं हो सका।',
    'clinical.allergyBanner': 'एलर्जी: {text}',
    'clinical.complaint': 'शिकायत',
    'clinical.vitals': 'वाइटल्स',
    'clinical.vitalsCount': 'वाइटल्स · {n}',
    'clinical.receipt': 'रसीद',
    'clinical.payment': 'भुगतान',
    'clinical.readings': 'रीडिंग',
    'clinical.newestLast': 'नई सबसे नीचे',
    'clinical.addingKeepsEarlier': 'नई रीडिंग जोड़ने पर पुरानी रीडिंग बनी रहती हैं — BP दोबारा नापना नई लाइन है, बदलाव नहीं।',
    'clinical.leaveBlank': 'जो नापा नहीं गया उसे खाली छोड़ दें। BMI अपने आप निकलता है, लिखा नहीं जाता।',
    'clinical.weightKg': 'वज़न (kg)',
    'clinical.heightCm': 'लंबाई (cm)',
    'clinical.systolic': 'सिस्टोलिक',
    'clinical.diastolic': 'डायस्टोलिक',
    'clinical.pulseBpm': 'पल्स (bpm)',
    'clinical.tempF': 'तापमान (°F)',
    'clinical.respRate': 'साँस की दर',
    'clinical.bloodSugar': 'ब्लड शुगर',
    'clinical.sugarType': 'शुगर का प्रकार',
    'clinical.fasting': 'फ़ास्टिंग (खाली पेट)',
    'clinical.postPrandial': 'खाने के बाद (PP)',
    'clinical.random': 'रैंडम',
    'clinical.addAnotherReading': 'एक और रीडिंग जोड़ें',
    'clinical.recordVitals': 'वाइटल्स दर्ज करें',
    'clinical.bookedToPay': 'बुकिंग में {mode} से भुगतान चुना था। अगर दूसरे तरीके से दे रहे हैं तो नीचे बदलें।',
    'clinical.bookedToPayQuoted': 'बुकिंग में {mode} से भुगतान चुना था, {amount} बताया गया था। अगर दूसरे तरीके से दे रहे हैं तो नीचे बदलें।',
    'clinical.consultationFee': 'कंसल्टेशन फ़ीस',
    'clinical.rateFor': '{doctor} की {type} फ़ीस',
    'clinical.discount': 'छूट',
    'clinical.discountReason': 'छूट का कारण',
    'clinical.netPayable': 'कुल देय',
    'clinical.amountPaid': 'दी गई राशि',
    'clinical.mode': 'तरीका',
    'clinical.referenceNo': 'रेफ़रेंस नं.',
    'clinical.referencePh': 'UPI ट्रांज़ैक्शन / कार्ड के आखिरी 4 अंक',
    'clinical.recording': 'दर्ज हो रहा है…',
    'clinical.recordPayment': 'भुगतान दर्ज करें',
    'clinical.reference': 'रेफ़रेंस',
    'clinical.balanceDue': 'बाकी राशि',
    'clinical.paymentReceipt': 'भुगतान रसीद',
    'clinical.paymentsFinal': 'भुगतान बदले नहीं जा सकते। रिफ़ंड या सुधार अलग एडजस्टमेंट के रूप में दर्ज होता है।',
    'clinical.latestVitals': 'फ़ाइल में ताज़ा वाइटल्स: {list}।',
    'clinical.latestVitalsRecorded': 'फ़ाइल में ताज़ा वाइटल्स: दर्ज हैं।',
    'clinical.latestPulse': 'पल्स {v}',
  },
  mr: {
    'clinical.visitTitle': 'भेट',
    'clinical.saveDraft': 'ड्राफ्ट जतन करा',
    'clinical.moreHistory': 'इतिहास आणि तपासणी',
    'clinical.addMedicine': 'औषध जोडा',
    'clinical.addTest': 'तपासणी जोडा',
    'clinical.vPulseLabel': 'नाडी',
    'clinical.vTempLabel': 'तापमान',
    'clinical.vRoomAir': 'सामान्य हवा',
    'clinical.token': 'टोकन #{n}',
    'clinical.visitNo': 'भेट #{n}',
    'clinical.couldNotSave': 'सेव्ह झाले नाही.',
    'clinical.chronicNote': 'दीर्घ आजार: {text}',
    'clinical.vWt': 'वजन {v}',
    'clinical.vWtKg': 'वजन {v} kg',
    'clinical.vHtCm': 'उंची {v} cm',
    'clinical.vPulse': 'पल्स {v}',
    'clinical.vTemp': 'तापमान {v}',
    'clinical.vTempF': 'तापमान {v}°F',
    'clinical.vSugar': 'शुगर {v} {type}',

    'clinical.consultingRoom': 'कन्सल्टिंग रूम',
    'clinical.todaysQueue': 'आजची रांग',
    'clinical.toSee': '{n} तपासायचे बाकी',
    'clinical.nobodyWaiting': 'कोणीही प्रतीक्षेत नाही',
    'clinical.nobodyWaitingBody': 'रिसेप्शन फ्रंट डेस्कवरून भेटी सुरू करते.',
    'clinical.couldNotOpenVisit': 'ही भेट उघडता आली नाही.',

    'clinical.reopen': 'पुन्हा उघडा',
    'clinical.working': 'होत आहे…',
    'clinical.closeAndPrint': 'बंद करा आणि प्रिंट करा',
    'clinical.draft': 'ड्राफ्ट',
    'clinical.backToQueue': 'रांगेकडे परत',
    'clinical.previewSheet': 'प्रिंट होणारी शीट पाहा',
    'clinical.noVitals': 'वाइटल्स नोंदवलेले नाहीत',
    'clinical.allergiesBanner': 'ॲलर्जी: {text}',
    'clinical.closedPrinted': 'कन्सल्टेशन बंद. आतापर्यंत {n} वेळा प्रिंट झाले.',
    'clinical.templates': 'टेम्पलेट',
    'clinical.removeOne': 'एक काढा',
    'clinical.repeatLast': 'मागील प्रिस्क्रिप्शन पुन्हा वापरा',
    'clinical.chiefComplaints': 'मुख्य तक्रारी',
    'clinical.hpi': 'सध्याच्या आजाराचा इतिहास',
    'clinical.examFindings': 'तपासणीतील निष्कर्ष',
    'clinical.provisionalDx': 'प्राथमिक डायग्नोसिस',
    'clinical.icd10': 'ICD-10 कोड',
    'clinical.pastHistory': 'पूर्वीचा इतिहास',
    'clinical.familyHistory': 'कौटुंबिक इतिहास',
    'clinical.medicines': 'औषधे',
    'clinical.onThePad': 'पॅडवर {n}',
    'clinical.checkAllergy': 'सही करण्यापूर्वी ॲलर्जीची नोंद तपासा',
    'clinical.allergyWarnNote': 'नोंदवलेली ॲलर्जी: {allergies}. हे फक्त नावाचे जुळणे आहे — या रुग्णासाठी सुरक्षित आहे हे तुम्हाला माहीत असल्यास तरीही सही करा.',
    'clinical.removeItem': '{name} काढा',
    'clinical.removeMedicineN': 'औषध {n} काढा',
    'clinical.removeTestN': 'तपासणी {n} काढा',
    'clinical.drugPlaceholder': 'ब्रँड किंवा जेनेरिक नाव',
    'clinical.drugNotListed': 'औषध यादीत नाही — जसे लिहिले तसेच प्रिंट होईल.',
    'clinical.allergyRow': 'ॲलर्जी: {text}',
    'clinical.morning': 'सकाळ',
    'clinical.afternoon': 'दुपार',
    'clinical.night': 'रात्र',
    'clinical.morningShort': 'स',
    'clinical.afternoonShort': 'दु',
    'clinical.nightShort': 'रा',
    'clinical.timing': 'वेळ',
    'clinical.timingPick': 'वेळ —',
    'clinical.durationDays': 'किती दिवस',
    'clinical.days': 'दिवस',
    'clinical.quantity': 'संख्या',
    'clinical.qty': 'संख्या',
    'clinical.note': 'नोंद',
    'clinical.noteOptional': 'नोंद (ऐच्छिक)',
    'clinical.dispense': 'द्या: {qty}',
    'clinical.testsAdvised': 'सुचवलेल्या तपासण्या',
    'clinical.ordered': '{n} तपासण्या लिहिल्या',
    'clinical.testName': 'तपासणीचे नाव',
    'clinical.preparation': 'तयारी: {text}',
    'clinical.urgency': 'तातडी',
    'clinical.instruction': 'सूचना',
    'clinical.advice': 'सल्ला',
    'clinical.adviceHint': 'आहार, हालचाल, धोक्याची लक्षणे.',
    'clinical.followUpDays': 'फॉलो-अप (किती दिवसांनी)',
    'clinical.printsAs': 'प्रिंटवर: {date}',
    'clinical.savePadAsTemplate': 'पॅड टेम्पलेट म्हणून सेव्ह करा',
    'clinical.templateName': 'टेम्पलेटचे नाव',
    'clinical.saveTemplate': 'टेम्पलेट सेव्ह करा',
    'clinical.saveAsTemplate': 'टेम्पलेट म्हणून सेव्ह करा',
    'clinical.addendum': 'ॲडेंडम',
    'clinical.addendumNote': 'सही केलेले प्रिस्क्रिप्शन कधीही बदलले जात नाही. दुरुस्त्या तारखेसह खाली जोडल्या जातात.',
    'clinical.addendumAria': 'ॲडेंडम नोंद',
    'clinical.addNote': 'नोंद जोडा',
    'clinical.append': 'जोडा',
    'clinical.previousVisits': 'मागील भेटी',
    'clinical.firstVisit': 'क्लिनिकमधील पहिली भेट.',
    'clinical.timesDays': '× {n} दिवस',
    'clinical.noMedicines': 'कोणतीही औषधे नोंदवलेली नाहीत.',
    'clinical.removeTemplateTitle': 'टेम्पलेट काढा',
    'clinical.removeTemplateNote': 'टेम्पलेट काढल्याने त्यावरून आधी लिहिलेल्या प्रिस्क्रिप्शनवर काहीही परिणाम होत नाही.',
    'clinical.keepThem': 'राहू द्या',
    'clinical.reopenTitle': 'बदलासाठी पुन्हा उघडा',
    'clinical.reopenNote': 'हे रेकॉर्डमध्ये तारखेसह ॲडेंडम म्हणून नोंदवले जाईल. भेट पुन्हा "डॉक्टरांकडे" होईल.',
    'clinical.reopenReasonAria': 'पुन्हा उघडण्याचे कारण',
    'clinical.reopenReasonPh': 'हे पुन्हा का उघडत आहात?',
    'clinical.keepClosed': 'बंदच राहू द्या',

    'clinical.draftSaved': 'ड्राफ्ट सेव्ह झाला.',
    'clinical.allergyConfirm': '{name} यांना {allergies} ची ॲलर्जी नोंदवलेली आहे.\n\n{conflicts}\n\nतरीही सही करून प्रिंट करायचे?',
    'clinical.signedPrinting': 'सही झाली. प्रिंटरकडे पाठवत आहे.',
    'clinical.reopened': 'पुन्हा उघडले. बदल करून पुन्हा बंद करा.',
    'clinical.addendumAdded': 'ॲडेंडम जोडले.',
    'clinical.templateSaved': 'टेम्पलेट म्हणून सेव्ह झाले.',
    'clinical.templateRemoved': '"{name}" काढले.',
    'clinical.templateApplied': '"{name}" लावले.',
    'clinical.carriedForward_one': '{date} पासून {n} औषध पुढे घेतले.',
    'clinical.carriedForward_other': '{date} पासून {n} औषधे पुढे घेतली.',

    'clinical.enterOneReading': 'किमान एक रीडिंग भरा.',
    'clinical.readingRecorded': 'रीडिंग नोंदवले.',
    'clinical.paymentRecorded': 'पेमेंट नोंदवले.',
    'clinical.couldNotRecordPayment': 'पेमेंट नोंदवता आले नाही.',
    'clinical.allergyBanner': 'ॲलर्जी: {text}',
    'clinical.complaint': 'तक्रार',
    'clinical.vitals': 'वाइटल्स',
    'clinical.vitalsCount': 'वाइटल्स · {n}',
    'clinical.receipt': 'पावती',
    'clinical.payment': 'पेमेंट',
    'clinical.readings': 'रीडिंग',
    'clinical.newestLast': 'नवीन सर्वात खाली',
    'clinical.addingKeepsEarlier': 'नवीन रीडिंग जोडल्यावर आधीचे रीडिंग तसेच राहतात — BP पुन्हा तपासणे म्हणजे नवी ओळ, बदल नाही.',
    'clinical.leaveBlank': 'जे मोजले नाही ते रिकामे ठेवा. BMI आपोआप काढला जातो, लिहिला जात नाही.',
    'clinical.weightKg': 'वजन (kg)',
    'clinical.heightCm': 'उंची (cm)',
    'clinical.systolic': 'सिस्टोलिक',
    'clinical.diastolic': 'डायस्टोलिक',
    'clinical.pulseBpm': 'पल्स (bpm)',
    'clinical.tempF': 'तापमान (°F)',
    'clinical.respRate': 'श्वसन दर',
    'clinical.bloodSugar': 'ब्लड शुगर',
    'clinical.sugarType': 'शुगरचा प्रकार',
    'clinical.fasting': 'फास्टिंग (उपाशीपोटी)',
    'clinical.postPrandial': 'जेवणानंतर (PP)',
    'clinical.random': 'रँडम',
    'clinical.addAnotherReading': 'आणखी एक रीडिंग जोडा',
    'clinical.recordVitals': 'वाइटल्स नोंदवा',
    'clinical.bookedToPay': 'बुकिंगमध्ये {mode} ने पैसे भरण्याचे निवडले होते. दुसऱ्या प्रकारे भरत असल्यास खाली बदला.',
    'clinical.bookedToPayQuoted': 'बुकिंगमध्ये {mode} ने पैसे भरण्याचे निवडले होते, {amount} सांगितले होते. दुसऱ्या प्रकारे भरत असल्यास खाली बदला.',
    'clinical.consultationFee': 'कन्सल्टेशन फी',
    'clinical.rateFor': '{doctor} यांची {type} फी',
    'clinical.discount': 'सवलत',
    'clinical.discountReason': 'सवलतीचे कारण',
    'clinical.netPayable': 'एकूण देय',
    'clinical.amountPaid': 'भरलेली रक्कम',
    'clinical.mode': 'पद्धत',
    'clinical.referenceNo': 'रेफरन्स क्र.',
    'clinical.referencePh': 'UPI व्यवहार / कार्डचे शेवटचे 4 अंक',
    'clinical.recording': 'नोंदवत आहे…',
    'clinical.recordPayment': 'पेमेंट नोंदवा',
    'clinical.reference': 'रेफरन्स',
    'clinical.balanceDue': 'बाकी रक्कम',
    'clinical.paymentReceipt': 'पेमेंट पावती',
    'clinical.paymentsFinal': 'पेमेंट बदलता येत नाही. परतावा किंवा दुरुस्ती स्वतंत्र ॲडजस्टमेंट म्हणून नोंदवली जाते.',
    'clinical.latestVitals': 'फाइलमधील ताजे वाइटल्स: {list}.',
    'clinical.latestVitalsRecorded': 'फाइलमधील ताजे वाइटल्स: नोंदवलेले आहेत.',
    'clinical.latestPulse': 'पल्स {v}',
  },
  or: {
    'clinical.visitTitle': 'ଭିଜିଟ୍',
    'clinical.saveDraft': 'ଡ୍ରାଫ୍ଟ ସେଭ୍',
    'clinical.moreHistory': 'ଇତିହାସ ଓ ପରୀକ୍ଷା',
    'clinical.addMedicine': 'ଔଷଧ ଯୋଡ଼ନ୍ତୁ',
    'clinical.addTest': 'ପରୀକ୍ଷା ଯୋଡ଼ନ୍ତୁ',
    'clinical.vPulseLabel': 'ନାଡ଼ି',
    'clinical.vTempLabel': 'ତାପମାତ୍ରା',
    'clinical.vRoomAir': 'ସାଧାରଣ ବାୟୁ',
    'clinical.token': 'ଟୋକେନ୍ #{n}',
    'clinical.visitNo': 'ପରିଦର୍ଶନ #{n}',
    'clinical.couldNotSave': 'ସେଭ୍ ହୋଇପାରିଲା ନାହିଁ।',
    'clinical.chronicNote': 'ପୁରୁଣା ରୋଗ: {text}',
    'clinical.vWt': 'ଓଜନ {v}',
    'clinical.vWtKg': 'ଓଜନ {v} kg',
    'clinical.vHtCm': 'ଉଚ୍ଚତା {v} cm',
    'clinical.vPulse': 'ପଲ୍ସ {v}',
    'clinical.vTemp': 'ତାପମାତ୍ରା {v}',
    'clinical.vTempF': 'ତାପମାତ୍ରା {v}°F',
    'clinical.vSugar': 'ସୁଗାର୍ {v} {type}',

    'clinical.consultingRoom': 'କନସଲଟିଂ ରୁମ୍',
    'clinical.todaysQueue': 'ଆଜିର ଧାଡ଼ି',
    'clinical.toSee': '{n} ଜଣ ଦେଖିବା ବାକି',
    'clinical.nobodyWaiting': 'କେହି ଅପେକ୍ଷାରେ ନାହାନ୍ତି',
    'clinical.nobodyWaitingBody': 'ରିସେପସନ୍ ଫ୍ରଣ୍ଟ ଡେସ୍କରୁ ପରିଦର୍ଶନ ଖୋଲେ।',
    'clinical.couldNotOpenVisit': 'ଏହି ପରିଦର୍ଶନ ଖୋଲିପାରିଲା ନାହିଁ।',

    'clinical.reopen': 'ପୁଣି ଖୋଲନ୍ତୁ',
    'clinical.working': 'ହେଉଛି…',
    'clinical.closeAndPrint': 'ବନ୍ଦ କରି ପ୍ରିଣ୍ଟ କରନ୍ତୁ',
    'clinical.draft': 'ଡ୍ରାଫ୍ଟ',
    'clinical.backToQueue': 'ଧାଡ଼ିକୁ ଫେରନ୍ତୁ',
    'clinical.previewSheet': 'ପ୍ରିଣ୍ଟ ହେବାକୁ ଥିବା ସିଟ୍ ଦେଖନ୍ତୁ',
    'clinical.noVitals': 'କୌଣସି ଭାଇଟାଲ୍ସ ଲେଖାଯାଇନାହିଁ',
    'clinical.allergiesBanner': 'ଆଲର୍ଜି: {text}',
    'clinical.closedPrinted': 'କନସଲଟେସନ୍ ବନ୍ଦ। ଏପର୍ଯ୍ୟନ୍ତ {n} ଥର ପ୍ରିଣ୍ଟ ହୋଇଛି।',
    'clinical.templates': 'ଟେମ୍ପଲେଟ୍',
    'clinical.removeOne': 'ଗୋଟିଏ ହଟାନ୍ତୁ',
    'clinical.repeatLast': 'ଶେଷ ପ୍ରେସକ୍ରିପସନ୍ ପୁଣି ଦିଅନ୍ତୁ',
    'clinical.chiefComplaints': 'ମୁଖ୍ୟ ଅଭିଯୋଗ',
    'clinical.hpi': 'ବର୍ତ୍ତମାନ ରୋଗର ଇତିହାସ',
    'clinical.examFindings': 'ପରୀକ୍ଷାର ଫଳାଫଳ',
    'clinical.provisionalDx': 'ପ୍ରାଥମିକ ଡାଇଗ୍ନୋସିସ୍',
    'clinical.icd10': 'ICD-10 କୋଡ୍',
    'clinical.pastHistory': 'ପୂର୍ବ ଇତିହାସ',
    'clinical.familyHistory': 'ପାରିବାରିକ ଇତିହାସ',
    'clinical.medicines': 'ଔଷଧ',
    'clinical.onThePad': 'ପ୍ୟାଡରେ {n}',
    'clinical.checkAllergy': 'ସାଇନ୍ କରିବା ପୂର୍ବରୁ ଆଲର୍ଜି ନୋଟ୍ ଯାଞ୍ଚ କରନ୍ତୁ',
    'clinical.allergyWarnNote': 'ଲେଖାଯାଇଥିବା ଆଲର୍ଜି: {allergies}। ଏହା କେବଳ ନାମ ମେଳ — ଏହି ରୋଗୀଙ୍କ ପାଇଁ ଏହା ସୁରକ୍ଷିତ ବୋଲି ଆପଣ ଜାଣିଥିଲେ ତଥାପି ସାଇନ୍ କରନ୍ତୁ।',
    'clinical.removeItem': '{name} ହଟାନ୍ତୁ',
    'clinical.removeMedicineN': 'ଔଷଧ {n} ହଟାନ୍ତୁ',
    'clinical.removeTestN': 'ପରୀକ୍ଷା {n} ହଟାନ୍ତୁ',
    'clinical.drugPlaceholder': 'ବ୍ରାଣ୍ଡ କିମ୍ବା ଜେନେରିକ୍ ନାମ',
    'clinical.drugNotListed': 'ଔଷଧ ତାଲିକାରେ ନାହିଁ — ଯେପରି ଲେଖାଯାଇଛି ସେପରି ପ୍ରିଣ୍ଟ ହେବ।',
    'clinical.allergyRow': 'ଆଲର୍ଜି: {text}',
    'clinical.morning': 'ସକାଳ',
    'clinical.afternoon': 'ମଧ୍ୟାହ୍ନ',
    'clinical.night': 'ରାତି',
    'clinical.morningShort': 'ସ',
    'clinical.afternoonShort': 'ମ',
    'clinical.nightShort': 'ରା',
    'clinical.timing': 'ସମୟ',
    'clinical.timingPick': 'ସମୟ —',
    'clinical.durationDays': 'କେତେ ଦିନ',
    'clinical.days': 'ଦିନ',
    'clinical.quantity': 'ପରିମାଣ',
    'clinical.qty': 'ପରିମାଣ',
    'clinical.note': 'ନୋଟ୍',
    'clinical.noteOptional': 'ନୋଟ୍ (ଇଚ୍ଛାଧୀନ)',
    'clinical.dispense': 'ଦିଅନ୍ତୁ: {qty}',
    'clinical.testsAdvised': 'ପରାମର୍ଶିତ ପରୀକ୍ଷା',
    'clinical.ordered': '{n}ଟି ପରୀକ୍ଷା ଲେଖାଗଲା',
    'clinical.testName': 'ପରୀକ୍ଷାର ନାମ',
    'clinical.preparation': 'ପ୍ରସ୍ତୁତି: {text}',
    'clinical.urgency': 'ଜରୁରୀତା',
    'clinical.instruction': 'ନିର୍ଦ୍ଦେଶ',
    'clinical.advice': 'ପରାମର୍ଶ',
    'clinical.adviceHint': 'ଖାଦ୍ୟ, କାର୍ଯ୍ୟକଳାପ, ବିପଦ ସଙ୍କେତ।',
    'clinical.followUpDays': 'ଫଲୋ-ଅପ୍ (କେତେ ଦିନ ପରେ)',
    'clinical.printsAs': 'ପ୍ରିଣ୍ଟରେ: {date}',
    'clinical.savePadAsTemplate': 'ପ୍ୟାଡକୁ ଟେମ୍ପଲେଟ୍ ଭାବେ ସେଭ୍ କରନ୍ତୁ',
    'clinical.templateName': 'ଟେମ୍ପଲେଟ୍ ନାମ',
    'clinical.saveTemplate': 'ଟେମ୍ପଲେଟ୍ ସେଭ୍ କରନ୍ତୁ',
    'clinical.saveAsTemplate': 'ଟେମ୍ପଲେଟ୍ ଭାବେ ସେଭ୍ କରନ୍ତୁ',
    'clinical.addendum': 'ଆଡେଣ୍ଡମ୍',
    'clinical.addendumNote': 'ସାଇନ୍ ହୋଇଥିବା ପ୍ରେସକ୍ରିପସନ୍ କେବେ ବଦଳାଯାଏ ନାହିଁ। ସଂଶୋଧନ ତାରିଖ ସହ ତଳେ ଯୋଡାଯାଏ।',
    'clinical.addendumAria': 'ଆଡେଣ୍ଡମ୍ ନୋଟ୍',
    'clinical.addNote': 'ନୋଟ୍ ଯୋଡନ୍ତୁ',
    'clinical.append': 'ଯୋଡନ୍ତୁ',
    'clinical.previousVisits': 'ପୂର୍ବ ପରିଦର୍ଶନ',
    'clinical.firstVisit': 'କ୍ଲିନିକ୍‌କୁ ପ୍ରଥମ ପରିଦର୍ଶନ।',
    'clinical.timesDays': '× {n} ଦିନ',
    'clinical.noMedicines': 'କୌଣସି ଔଷଧ ଲେଖାଯାଇନାହିଁ।',
    'clinical.removeTemplateTitle': 'ଟେମ୍ପଲେଟ୍ ହଟାନ୍ତୁ',
    'clinical.removeTemplateNote': 'ଟେମ୍ପଲେଟ୍ ହଟାଇଲେ ସେଥିରୁ ପୂର୍ବରୁ ଲେଖାଯାଇଥିବା କୌଣସି ପ୍ରେସକ୍ରିପସନ୍ ଉପରେ ପ୍ରଭାବ ପଡ଼େ ନାହିଁ।',
    'clinical.keepThem': 'ରହିବାକୁ ଦିଅନ୍ତୁ',
    'clinical.reopenTitle': 'ବଦଳାଇବା ପାଇଁ ପୁଣି ଖୋଲନ୍ତୁ',
    'clinical.reopenNote': 'ଏହା ରେକର୍ଡରେ ତାରିଖ ସହ ଆଡେଣ୍ଡମ୍ ଭାବେ ରହିବ। ପରିଦର୍ଶନ ପୁଣି "ଡାକ୍ତରଙ୍କ ପାଖରେ" ହେବ।',
    'clinical.reopenReasonAria': 'ପୁଣି ଖୋଲିବାର କାରଣ',
    'clinical.reopenReasonPh': 'ଏହାକୁ କାହିଁକି ପୁଣି ଖୋଲୁଛନ୍ତି?',
    'clinical.keepClosed': 'ବନ୍ଦ ହିଁ ରଖନ୍ତୁ',

    'clinical.draftSaved': 'ଡ୍ରାଫ୍ଟ ସେଭ୍ ହେଲା।',
    'clinical.allergyConfirm': '{name}ଙ୍କର {allergies} ପ୍ରତି ଆଲର୍ଜି ଲେଖାଯାଇଛି।\n\n{conflicts}\n\nତଥାପି ସାଇନ୍ କରି ପ୍ରିଣ୍ଟ କରିବେ?',
    'clinical.signedPrinting': 'ସାଇନ୍ ହେଲା। ପ୍ରିଣ୍ଟରକୁ ପଠାଯାଉଛି।',
    'clinical.reopened': 'ପୁଣି ଖୋଲିଲା। ବଦଳାଇ ସାରିବା ପରେ ପୁଣି ବନ୍ଦ କରନ୍ତୁ।',
    'clinical.addendumAdded': 'ଆଡେଣ୍ଡମ୍ ଯୋଡାଗଲା।',
    'clinical.templateSaved': 'ଟେମ୍ପଲେଟ୍ ଭାବେ ସେଭ୍ ହେଲା।',
    'clinical.templateRemoved': '"{name}" ହଟାଗଲା।',
    'clinical.templateApplied': '"{name}" ଲଗାଗଲା।',
    'clinical.carriedForward_one': '{date}ରୁ {n}ଟି ଔଷଧ ଆଗକୁ ନିଆଗଲା।',
    'clinical.carriedForward_other': '{date}ରୁ {n}ଟି ଔଷଧ ଆଗକୁ ନିଆଗଲା।',

    'clinical.enterOneReading': 'ଅତି କମରେ ଗୋଟିଏ ରିଡିଂ ଦିଅନ୍ତୁ।',
    'clinical.readingRecorded': 'ରିଡିଂ ଲେଖାଗଲା।',
    'clinical.paymentRecorded': 'ଦେୟ ଲେଖାଗଲା।',
    'clinical.couldNotRecordPayment': 'ଦେୟ ଲେଖାଯାଇପାରିଲା ନାହିଁ।',
    'clinical.allergyBanner': 'ଆଲର୍ଜି: {text}',
    'clinical.complaint': 'ଅଭିଯୋଗ',
    'clinical.vitals': 'ଭାଇଟାଲ୍ସ',
    'clinical.vitalsCount': 'ଭାଇଟାଲ୍ସ · {n}',
    'clinical.receipt': 'ରସିଦ',
    'clinical.payment': 'ଦେୟ',
    'clinical.readings': 'ରିଡିଂ',
    'clinical.newestLast': 'ନୂଆ ସବୁଠୁ ତଳେ',
    'clinical.addingKeepsEarlier': 'ନୂଆ ରିଡିଂ ଯୋଡିଲେ ପୁରୁଣା ରିଡିଂ ରହିଯାଏ — BP ପୁଣି ମାପିବା ଏକ ନୂଆ ଧାଡ଼ି, ବଦଳ ନୁହେଁ।',
    'clinical.leaveBlank': 'ଯାହା ମପାଯାଇନାହିଁ ତାହା ଖାଲି ଛାଡନ୍ତୁ। BMI ନିଜେ ହିସାବ ହୁଏ, ଲେଖାଯାଏ ନାହିଁ।',
    'clinical.weightKg': 'ଓଜନ (kg)',
    'clinical.heightCm': 'ଉଚ୍ଚତା (cm)',
    'clinical.systolic': 'ସିଷ୍ଟୋଲିକ୍',
    'clinical.diastolic': 'ଡାୟାଷ୍ଟୋଲିକ୍',
    'clinical.pulseBpm': 'ପଲ୍ସ (bpm)',
    'clinical.tempF': 'ତାପମାତ୍ରା (°F)',
    'clinical.respRate': 'ଶ୍ୱାସ ହାର',
    'clinical.bloodSugar': 'ବ୍ଲଡ୍ ସୁଗାର୍',
    'clinical.sugarType': 'ସୁଗାର୍ ପ୍ରକାର',
    'clinical.fasting': 'ଫାଷ୍ଟିଂ (ଖାଲି ପେଟ)',
    'clinical.postPrandial': 'ଖାଇବା ପରେ (PP)',
    'clinical.random': 'ର୍ୟାଣ୍ଡମ୍',
    'clinical.addAnotherReading': 'ଆଉ ଏକ ରିଡିଂ ଯୋଡନ୍ତୁ',
    'clinical.recordVitals': 'ଭାଇଟାଲ୍ସ ଲେଖନ୍ତୁ',
    'clinical.bookedToPay': 'ବୁକିଂରେ {mode} ଦ୍ୱାରା ଦେୟ ବାଛିଥିଲେ। ଅନ୍ୟ ଉପାୟରେ ଦେଉଥିଲେ ତଳେ ବଦଳାନ୍ତୁ।',
    'clinical.bookedToPayQuoted': 'ବୁକିଂରେ {mode} ଦ୍ୱାରା ଦେୟ ବାଛିଥିଲେ, {amount} କୁହାଯାଇଥିଲା। ଅନ୍ୟ ଉପାୟରେ ଦେଉଥିଲେ ତଳେ ବଦଳାନ୍ତୁ।',
    'clinical.consultationFee': 'କନସଲଟେସନ୍ ଫି',
    'clinical.rateFor': '{doctor}ଙ୍କ {type} ଫି',
    'clinical.discount': 'ରିହାତି',
    'clinical.discountReason': 'ରିହାତିର କାରଣ',
    'clinical.netPayable': 'ମୋଟ ଦେୟ',
    'clinical.amountPaid': 'ଦିଆଯାଇଥିବା ରାଶି',
    'clinical.mode': 'ଉପାୟ',
    'clinical.referenceNo': 'ରେଫରେନ୍ସ ନଂ.',
    'clinical.referencePh': 'UPI ଟ୍ରାଞ୍ଜାକସନ୍ / କାର୍ଡର ଶେଷ 4 ଅଙ୍କ',
    'clinical.recording': 'ଲେଖାଯାଉଛି…',
    'clinical.recordPayment': 'ଦେୟ ଲେଖନ୍ତୁ',
    'clinical.reference': 'ରେଫରେନ୍ସ',
    'clinical.balanceDue': 'ବାକି ରାଶି',
    'clinical.paymentReceipt': 'ଦେୟ ରସିଦ',
    'clinical.paymentsFinal': 'ଦେୟ ବଦଳାଯାଇପାରିବ ନାହିଁ। ରିଫଣ୍ଡ କିମ୍ବା ସଂଶୋଧନ ଅଲଗା ଆଡଜଷ୍ଟମେଣ୍ଟ ଭାବେ ଲେଖାଯାଏ।',
    'clinical.latestVitals': 'ଫାଇଲରେ ଥିବା ନୂଆ ଭାଇଟାଲ୍ସ: {list}।',
    'clinical.latestVitalsRecorded': 'ଫାଇଲରେ ଥିବା ନୂଆ ଭାଇଟାଲ୍ସ: ଲେଖାଯାଇଛି।',
    'clinical.latestPulse': 'ପଲ୍ସ {v}',
  },
})
